import {
  BlockRegistry,
  EditorStore,
  createTemplate,
  migrateTemplate,
  mergeTheme,
  validateTemplate,
  checkOutput,
  resolveTemplate,
  type BlockDefinition,
  type DataSource,
  type FieldSchema,
  type Issue,
  type ReportData,
  type ReportTemplate,
  type Theme,
  type AnyVersionTemplate,
  type EditorState,
  type ResolvedNode
} from "@broadpaper/core";
import { builtInBlocks } from "@broadpaper/blocks";
import { DomMeasurer, layoutDocumentAsync, waitForFonts, ensureStyle, fontFaceCss, type AsyncMeasurer, type LayoutResult } from "@broadpaper/renderer";
import { Observable } from "./observable.js";
import { AiController } from "./ai.js";
import { OptionsStore, DEFAULT_FEATURES, normaliseSampleData, type EditorMeasurer, type FeatureFlags, type ReportDesignerOptions, type SampleDataSet } from "../options.js";

export type LeftTab = "blocks" | "layouts" | "data" | "layers" | "library" | "ai";

/**
 * A field on its way from the data panel to the page.
 *
 * `bound` is `expr` with the field's own formatter appended, and `schema` is the
 * field itself, so the block receiving the drop can decide what to build from it
 * rather than being handed a type name and left to guess.
 */
export interface FieldDragPayload {
  expr: string;
  bound?: string;
  label: string;
  type?: string;
  schema?: FieldSchema;
}

export interface SaveState {
  status: "idle" | "saving" | "saved" | "error";
  /** When the last successful save finished. */
  at?: number;
  /** Why the last save failed. */
  message?: string;
}

export interface DragState {
  kind: "new" | "move" | "row" | "library" | "field";
  /** Block type for "new"; node id for "move"; layout for "row"; library id for "library"; expression for "field". */
  payload: string | number[] | FieldDragPayload;
  label: string;
  icon?: string;
  x: number;
  y: number;
}

export interface DropTarget {
  /** Parent node id (container) or null for the region root. */
  parentId: string | null;
  region: "body" | "header" | "header:first" | "footer" | "footer:first";
  index: number;
  /** Indicator geometry in canvas-scroll coordinates. */
  line: { x: number; y: number; width: number } | null;
  zone: { x: number; y: number; width: number; height: number } | null;
  valid: boolean;
  /** For field drops onto text blocks. */
  textTargetId?: string;
}

export interface UiState {
  leftTab: LeftTab;
  leftOpen: boolean;
  rightOpen: boolean;
  guides: boolean;
  activeThemeId: string | null;
  activeSampleId: string | null;
  drag: DragState | null;
  dropTarget: DropTarget | null;
  issues: Issue[];
  toast: { id: number; message: string; tone?: "info" | "error" | "success" } | null;
  inspectorTab: "content" | "style";
  exporting: boolean;
  /**
   * What the save button last did. Separate from `dirty`, which answers
   * "is there anything to save"; this answers "what happened when I tried".
   */
  save: SaveState;
  /** Panel visibility to restore when leaving preview. */
  panelsBeforePreview: { leftOpen: boolean; rightOpen: boolean } | null;
  /**
   * Whether there is room for the panels beside the canvas.
   *
   * Measured from the designer's own element, not the window, because it is
   * embedded and the window says nothing about how much room it was given.
   * When this is true the panels are drawers over the canvas rather than
   * columns next to it, and only one can be open at a time — which is why the
   * flag lives here rather than staying local to the shell that measures it.
   */
  narrow: boolean;
  /** Panel widths in pixels, as dragged by the user. */
  leftWidth: number;
  rightWidth: number;
  /** Increments to force the canvas to scroll a node into view. */
  revealRequest: { id: string; seq: number } | null;
  /** Where a click in the canvas placed the insertion caret, if anywhere. */
  insertion: InsertionPoint | null;
}

/**
 * A position in the document chosen by clicking rather than by selecting.
 *
 * Selection answers "what am I editing"; this answers "where does the next
 * block go". Keeping them apart is what lets you click a gap between two
 * blocks and insert there without first selecting one of them.
 */
export interface InsertionPoint {
  parentId: string | null;
  region: DropTarget["region"];
  index: number;
  line: { x: number; y: number; width: number };
}

/**
 * Everything one mounted designer needs. Framework-neutral; the React tree only
 * subscribes to it.
 */
export class EditorSession {
  readonly options: OptionsStore;
  readonly store: EditorStore;
  readonly registry: BlockRegistry;
  readonly ui: Observable<UiState>;
  readonly layout = new Observable<LayoutResult | null>(null);
  readonly layoutVersion = new Observable(0);
  /** AI template design. Idle unless the host passed an `ai` provider. */
  readonly ai: AiController = new AiController(this);
  private measurer: DomMeasurer | null = null;
  private previewMeasurer: EditorMeasurer | null = null;
  private layoutQueued = false;
  private validateTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  readonly doc: Document;
  private readonly unsubscribe: Array<() => void> = [];

  constructor(doc: Document, options: ReportDesignerOptions) {
    this.doc = doc;
    this.options = new OptionsStore(options);
    this.registry = new BlockRegistry([...builtInBlocks, ...(options.blocks ?? [])], { enabledTiers: options.enabledTiers });
    const template = loadTemplate(options.template);
    this.store = new EditorStore({
      template,
      onChange: (t, entry) => {
        this.options.get().onChange?.(t, { reason: entry?.label ?? "change" });
      }
    });
    const samples = normaliseSampleData(options.sampleData);
    this.ui = new Observable<UiState>({
      leftTab: "blocks",
      leftOpen: true,
      rightOpen: true,
      guides: false,
      activeThemeId: options.theme?.id ?? options.themes?.[0]?.id ?? null,
      activeSampleId: samples[0]?.id ?? null,
      drag: null,
      dropTarget: null,
      issues: [],
      toast: null,
      inspectorTab: "content",
      exporting: false,
      save: { status: "idle" },
      panelsBeforePreview: null,
      narrow: false,
      leftWidth: 272,
      rightWidth: 296,
      revealRequest: null,
      insertion: null
    });

    this.unsubscribe.push(
      this.store.subscribe((state, prev) => {
        if (state.template !== prev.template || state.mode !== prev.mode) this.requestLayout();
        if (state.template !== prev.template) this.scheduleValidation();
        if (state.selection !== prev.selection) this.options.get().onSelectionChange?.(state.selection);
        if (state.mode !== prev.mode) this.options.get().onModeChange?.(state.mode);
      })
    );
    this.unsubscribe.push(this.options.subscribe(() => this.requestLayout()));
    this.unsubscribe.push(
      this.ui.subscribe(() => {
        /* theme / sample switches trigger layout via explicit calls */
      })
    );
    this.unsubscribe.push(this.registry.subscribe(() => this.requestLayout()));
  }

  /* ------------------------------------------------------------ */

  get features(): FeatureFlags {
    return { ...DEFAULT_FEATURES, ...(this.options.get().features ?? {}) };
  }

  get dataSources(): DataSource[] {
    return this.options.get().dataSources ?? [];
  }

  get themes(): Theme[] {
    const o = this.options.get();
    const list = [...(o.themes ?? [])];
    if (o.theme && !list.some((t) => t.id === o.theme!.id)) list.unshift(o.theme);
    return list;
  }

  get activeTheme(): Theme {
    const id = this.ui.get().activeThemeId;
    const found = this.themes.find((t) => t.id === id) ?? this.options.get().theme ?? this.themes[0];
    return mergeTheme(found);
  }

  get sampleSets(): SampleDataSet[] {
    return normaliseSampleData(this.options.get().sampleData);
  }

  get activeSample(): ReportData {
    const id = this.ui.get().activeSampleId;
    const sets = this.sampleSets;
    return (sets.find((s) => s.id === id) ?? sets[0])?.data ?? {};
  }

  setTheme(id: string): void {
    this.ui.patch({ activeThemeId: id });
    this.options.get().onThemeChange?.(this.activeTheme);
    this.requestLayout();
  }

  setSample(id: string): void {
    this.ui.patch({ activeSampleId: id });
    this.requestLayout();
    // Output warnings are about this data set, so they change with it.
    this.scheduleValidation();
  }

  /**
   * Switches mode, taking the editing furniture with it.
   *
   * Preview answers "what does the reader get", and a block palette and an
   * empty properties panel are not part of that answer — they take a third of
   * the width to say nothing. The panels are restored on the way back rather
   * than reset, because whichever ones you had open is a preference you set
   * once and should not have to set again after every preview.
   */
  setMode(mode: "design" | "preview"): void {
    const state = this.store.getState();
    if (mode === state.mode) return;
    const u = this.ui.get();
    if (mode === "preview") {
      this.ui.patch({ panelsBeforePreview: { leftOpen: u.leftOpen, rightOpen: u.rightOpen }, leftOpen: false, rightOpen: false });
    } else if (u.panelsBeforePreview) {
      this.ui.patch({ ...u.panelsBeforePreview, panelsBeforePreview: null });
    }
    this.store.setMode(mode);
  }

  toast(message: string, tone: "info" | "error" | "success" = "info"): void {
    const id = Date.now();
    this.ui.patch({ toast: { id, message, tone } });
    setTimeout(() => {
      if (this.ui.get().toast?.id === id) this.ui.patch({ toast: null });
    }, 2600);
  }

  /**
   * Applies a zoom fit and lands the viewport on a whole page.
   *
   * The toolbar owns the button; only the canvas knows the scroller, the
   * padding around the page stack and which page you were looking at, so the
   * canvas registers the implementation here and the toolbar asks for it.
   */
  fit: ((mode: "width" | "page") => void) | null = null;

  reveal(id: string): void {
    this.ui.patch({ revealRequest: { id, seq: (this.ui.get().revealRequest?.seq ?? 0) + 1 } });
  }

  /* ------------------------------ layout ------------------------------ */

  attachMeasurer(container: HTMLElement): void {
    this.measurer?.destroy();
    this.previewMeasurer?.destroy?.();
    this.measurer = new DomMeasurer(this.doc, container);
    // A host measurer paginates the canvas exactly as the PDF will, but only
    // design mode's own affordances stand in its way — badges, dashed outlines
    // and drop targets are styled by the document stylesheet, which an engine
    // outside the browser never sees and so cannot measure. Design mode
    // therefore measures with the browser, which is what draws it; preview
    // mode, which draws what the PDF draws, measures with the engine.
    const make = this.options.get().createMeasurer;
    this.previewMeasurer = make ? make({ document: this.doc, container }) : null;
    // Web fonts arriving after the first layout change line breaks: re-measure everything.
    const fonts = (this.doc as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts && !this.fontListener) {
      this.fontListener = () => {
        this.measurer?.clearCache();
        this.previewMeasurer?.clearCache?.();
        this.requestLayout();
      };
      fonts.addEventListener("loadingdone", this.fontListener);
    }
    this.requestLayout();
  }
  private fontListener: (() => void) | null = null;

  requestLayout(): void {
    if (this.layoutQueued || this.disposed) return;
    this.layoutQueued = true;
    const run = () => {
      this.layoutQueued = false;
      void this.runLayout();
    };
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
    else setTimeout(run, 0);
  }

  private layoutSeq = 0;
  private async runLayout(): Promise<void> {
    if (!this.measurer || this.disposed) return;
    const seq = ++this.layoutSeq;
    const theme = this.activeTheme;
    ensureStyle(this.doc, "bp-theme-fonts", fontFaceCss(theme));
    const families = [...(theme.fonts ?? []).map((f) => f.family), ...themeFontFamilies(theme)];
    await waitForFonts(this.doc, families);
    if (seq !== this.layoutSeq || this.disposed) return;
    const state = this.store.getState();
    const o = this.options.get();
    try {
      // One pipeline for both kinds of measurer: the DOM answers immediately,
      // an engine-backed one takes a moment, and awaiting costs the former
      // nothing that this already-async method was not paying anyway.
      const m: EditorMeasurer = (state.mode === "preview" && this.previewMeasurer) || this.measurer;
      const measurer: AsyncMeasurer = {
        setOptions: (opts) => m.setOptions?.(opts),
        measure: async (sections, width) => m.measure(sections, width)
      };
      const result = await layoutDocumentAsync(
        {
          template: state.template,
          registry: this.registry,
          theme,
          dataSources: this.dataSources,
          data: state.mode === "preview" ? this.activeSample : undefined,
          sampleData: this.activeSample,
          mode: state.mode,
          now: o.now,
          locale: o.locale,
          currency: o.currency,
          timeZone: o.timeZone,
          allowStyleOverrides: this.features.styleOverrides,
          minPages: 1
        },
        measurer
      );
      // A newer layout may have finished while the engine was measuring.
      if (seq !== this.layoutSeq || this.disposed) return;
      this.layout.set(result);
      this.layoutVersion.set((v) => v + 1);
      // Measured before a logo had decoded or a face had loaded, the canvas is
      // shorter than it draws, and a later font event is the only thing that would
      // ever have measured it again — which is how the same design showed two pages
      // in one tab and three in another. Lay out again once it has all arrived.
      if (m.settled) {
        void m.settled().then((arrived) => {
          if (arrived && seq === this.layoutSeq && !this.disposed) this.requestLayout();
        });
      }
    } catch (e) {
      console.error("[broadpaper] layout failed", e);
    }
  }

  /* ---------------------------- validation ---------------------------- */

  scheduleValidation(): void {
    if (this.validateTimer) clearTimeout(this.validateTimer);
    this.validateTimer = setTimeout(() => this.validateNow(), 250);
  }

  validateNow(): Issue[] {
    if (!this.features.validation) return [];
    const template = validateTemplate({ template: this.store.template, registry: this.registry, dataSources: this.dataSources, theme: this.activeTheme, skipSchema: true }).issues.map((i) => ({ ...i, source: i.source ?? ("template" as const) }));
    const issues = [...template, ...this.outputIssues()];
    this.ui.patch({ issues });
    this.options.get().onValidate?.(issues);
    return issues;
  }

  /**
   * What the active sample data does to the report, as opposed to whether the
   * template is sound. Needs its own preview resolution: the canvas is resolved
   * in design mode, which fills gaps in the sample with synthesised values and
   * would hide exactly the blanks we are looking for.
   */
  private outputIssues(): Issue[] {
    const sets = this.sampleSets;
    if (sets.length === 0) return [];
    const active = sets.find((s) => s.id === this.ui.get().activeSampleId) ?? sets[0]!;
    const o = this.options.get();
    try {
      const resolved = resolveTemplate({
        template: this.store.template,
        registry: this.registry,
        theme: this.activeTheme,
        dataSources: this.dataSources,
        data: active.data,
        mode: "preview",
        now: o.now,
        locale: o.locale,
        currency: o.currency,
        timeZone: o.timeZone,
        allowStyleOverrides: this.features.styleOverrides
      });
      return checkOutput({ resolved, registry: this.registry, dataLabel: active.label });
    } catch {
      // A template that cannot resolve is already an error the structural pass
      // reports; failing to also list its blanks is not worth a broken panel.
      return [];
    }
  }

  /* ------------------------------ helpers ----------------------------- */

  resolvedNode(id: string): ResolvedNode | undefined {
    const l = this.layout.get();
    if (!l) return undefined;
    for (const n of l.nodesByKey.values()) if (n.nodeId === id) return n;
    return undefined;
  }

  registerBlock(def: BlockDefinition<any>): void {
    if (this.registry.has(def.type)) this.registry.replace(def);
    else this.registry.register(def);
  }

  setTemplate(t: ReportTemplate | AnyVersionTemplate): void {
    this.store.load(loadTemplate(t));
    this.scheduleValidation();
  }

  getState(): EditorState {
    return this.store.getState();
  }

  destroy(): void {
    this.disposed = true;
    this.ai.destroy();
    for (const u of this.unsubscribe) u();
    this.measurer?.destroy();
    this.previewMeasurer?.destroy?.();
    if (this.validateTimer) clearTimeout(this.validateTimer);
    const fonts = (this.doc as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts && this.fontListener) fonts.removeEventListener("loadingdone", this.fontListener);
  }
}

/** Font family names referenced by a theme's typography (first family of each stack). */
function themeFontFamilies(theme: Theme): string[] {
  const out: string[] = [];
  for (const stack of [theme.typography.headingFont, theme.typography.bodyFont, theme.typography.monoFont ?? ""]) {
    const first = stack.split(",")[0]?.trim().replace(/^['"]|['"]$/g, "");
    if (first && !["system-ui", "sans-serif", "serif", "monospace"].includes(first)) out.push(first);
  }
  return out;
}

export function loadTemplate(input: ReportTemplate | AnyVersionTemplate | undefined): ReportTemplate {
  if (!input) return createTemplate();
  return migrateTemplate(input).template;
}
